/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onConnect,
  reaction,
  reatomAsync,
  sleep,
  spawn,
  withAbort,
} from '@reatom/framework'
import * as DateF from 'date-fns'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network/model'
import { fetchQuoteRequestAction } from '@root/entities/quote/atoms'
import { joinedInviteCodeAtom } from '@root/entities/referral'
import { environmentAtom, NumberUtil } from '@root/shared'
import { reatomAsyncState, reatomBigNumberComputed } from '@root/shared/lib'

import { tradeSettingsAtom } from '../trade-settings/atoms'

import type { Atom, Ctx } from '@reatom/framework'
import type { CurrencyModel } from '@root/entities/currency'
import type { Pair } from '@root/entities/pair'
import type { Quote } from '@root/entities/quote'
import type { NotEnoughLiquidityError } from '@root/entities/quote/atoms'
import type { WalletModel } from '@root/entities/wallet'
import type { Error } from '@root/shared'
import type { ApiError } from '@root/shared/api'
import type BigNumber from 'bignumber.js'

const INPUT_DEBOUNCE = 300

export type QuoteInput = {
  currencyIn: CurrencyModel | undefined
  currencyOut: CurrencyModel | undefined
  amount: BigNumber
  type: Quote.QuoteType
  pool: Pair.PairMinimal | undefined
  slippage: number
  wallet: WalletModel | undefined
}

type QuoteValue = Quote.Quote & {
  requestedAt: Date
}

export const reatomQuotePolling = (args: {
  name?: string
  skipLoadingState?: boolean
  inputAtom: Atom<QuoteInput>
}) => {
  const { name = 'unknown', skipLoadingState = true, inputAtom } = args

  const quoteStateAtom = reatomAsyncState.notInitiated<
    Error.UnknownError | ApiError.ApiError<string> | NotEnoughLiquidityError,
    QuoteValue
  >(`${name}.quoteStateAtom`)

  const quoteTypeAtom = atom(
    (ctx) => ctx.spy(inputAtom).type,
    `${name}.quoteTypeAtom`,
  )

  const poolAtom = atom((ctx) => ctx.spy(inputAtom).pool, `${name}.poolAtom`)

  const loadQuoteInternalAction = action(
    async (ctx: Ctx, requestedAt: Date) => {
      const currencyIn = ctx.get(inputAtom).currencyIn
      const currencyOut = ctx.get(inputAtom).currencyOut
      const pool = ctx.get(poolAtom)
      const amount = ctx.get(inputAtom).amount
      const quoteType = ctx.get(quoteTypeAtom)
      const wallet = ctx.get(inputAtom).wallet
      const settings = ctx.get(tradeSettingsAtom)

      if (
        !currencyIn ||
        !currencyOut ||
        !pool ||
        amount.isZero() ||
        settings.network !== Network.Solana
      ) {
        quoteStateAtom.setNotInitiated(ctx)
        return
      }

      const state = ctx.get(quoteStateAtom)
      if (AsyncStateN.isNotInitiated(state) || !skipLoadingState) {
        quoteStateAtom.setLoading(ctx)
      }

      const result = (
        await ctx.schedule(() =>
          fetchQuoteRequestAction(ctx, {
            currencyIn: currencyIn,
            currencyOut: currencyOut,
            amount: amount,
            type: quoteType,
            poolAddress: pool.address,
            providerId: pool.provider.id,
            network: pool.network,
            wallet: wallet,
            inviteCode: ctx.get(joinedInviteCodeAtom),
            settings,
          }),
        )
      ).pipe(
        Result.map(
          (it): QuoteValue => ({
            ...it,
            requestedAt,
          }),
        ),
      )

      const currentQuote = ctx.get(quoteStateAtom).value

      if (
        currentQuote &&
        DateF.isBefore(requestedAt, currentQuote.requestedAt)
      ) {
        return
      }

      quoteStateAtom.setResult(ctx, result)
    },
    `${name}.loadQuoteInternalAction`,
  )

  const startQuotePolling = reatomAsync(async (ctx: Ctx) => {
    const { quoteRefreshInterval } = ctx.get(environmentAtom)
    const load = () =>
      spawn(ctx, (ctx) =>
        ctx.schedule(() => loadQuoteInternalAction(ctx, new Date())),
      )

    await ctx.schedule(() => load())
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    while (true) {
      await ctx.schedule(() => sleep(quoteRefreshInterval * 1000))
      await ctx.schedule(() => load())
    }
    // eslint-disable-next-line no-unreachable
    return undefined
  }, `${name}.startQuotePolling`).pipe(withAbort())

  const amountAtom = reatomBigNumberComputed((ctx) => ctx.spy(inputAtom).amount)
  const currencyInAddress = atom(
    (ctx) => ctx.spy(inputAtom).currencyIn?.address,
    `${name}.currencyInAddress`,
  )
  const currencyOutAddress = atom(
    (ctx) => ctx.spy(inputAtom).currencyOut?.address,
    `${name}.currencyOutAddress`,
  )
  const poolAddress = atom(
    (ctx) => ctx.spy(poolAtom)?.address,
    `${name}.poolAddress`,
  )
  const slippage = atom(
    (ctx) => ctx.spy(inputAtom).slippage,
    `${name}.slippage`,
  )
  const walletAtom = atom((ctx) => ctx.spy(inputAtom).wallet, `${name}.wallet`)

  const currenciesChangeReaction = reaction((ctx) => {
    /**
     * When there is a change in these parameters immediately execute quote
     */
    const _in = ctx.spy(currencyInAddress)
    const _out = ctx.spy(currencyOutAddress)
    const _pool = ctx.spy(poolAddress)
    const _type = ctx.spy(quoteTypeAtom)
    const _slippage = ctx.spy(slippage)
    const _wallet = ctx.spy(walletAtom)
    startQuotePolling(ctx)
  }, `${name}.currenciesChangeReaction`)

  const amountChangeReaction = reaction(async (ctx) => {
    const amount = ctx.spy(amountAtom)
    if (amount.eq(0)) {
      quoteStateAtom.setNotInitiated(ctx)
      startQuotePolling.abort(ctx)
      return
    }

    await ctx.schedule(() => sleep(INPUT_DEBOUNCE))
    startQuotePolling(ctx)
  }, `${name}.amountChangeReaction`)

  onConnect(quoteStateAtom, (ctx) => {
    const subscriptions = [
      amountChangeReaction(ctx),
      currenciesChangeReaction(ctx),
    ]

    return () => {
      startQuotePolling.abort(ctx)
      for (const it of subscriptions) {
        it.unsubscribe()
      }
    }
  })

  const priceImpactAtom = atom((ctx) => {
    const quoteState = ctx.spy(quoteStateAtom)

    return pipe(
      quoteState,
      AsyncStateN.map((it) => NumberUtil.safeBigNumber(it.priceImpact, 0)),
    )
  }, `${name}.priceImpactAtom`)

  const outputAmountAtom = atom((ctx) => {
    const quoteState = ctx.spy(quoteStateAtom)

    return pipe(
      quoteState,
      AsyncStateN.map((it) => it.outputAmount),
    )
  }, `${name}.outputAmountAtom`)

  return {
    quoteStateAtom,
    forceAbortPolling: startQuotePolling.abort,
    forceStartPolling: startQuotePolling,
    priceImpact: priceImpactAtom,
    outputAmount: outputAmountAtom,
    input: inputAtom,
  }
}

export type QuotePollingModel = ReturnType<typeof reatomQuotePolling>
